import React, { useContext, useEffect, useState } from "react";
import { useMutation, useQuery } from "urql";
import GlobalContext from "../lib/GlobalContext";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import { navigate } from "hookrouter";
import withAccessControl from "../components/HOC/AccessControl";
import { MESSAGE_TYPES } from "../components/PopupMessages";
import { ACL_COMPONENTS } from "../config";
import clsx from "clsx";
import TextField from "@material-ui/core/TextField";
import GetSafe from "../lib/GetSafe";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Visibility from "@material-ui/icons/Visibility";
import Button from "@material-ui/core/Button";
import FormValueErrorsBuilder from "../lib/FormValueErrorsBuilder";
import Joi from "@hapi/joi";
import { green } from "@material-ui/core/colors";
import ExternalErrorLogger from "@ennit/react-external-errorlogger";
import FormatHelper from "../lib/FormatHelper";

/**
 * useStyles
 */
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  dialog: {
    width: "100%",
    paddingBottom: 56,
    "&::before": theme.watermarkLogo,
    [theme.breakpoints.down("sm")]: {
      padding: 20,
    },
  },
  title: {
    margin: 0,
    fontSize: 24,
    lineHeight: "30px",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      textAlign: "left",
    },
  },
  item: {
    width: "100%",
    maxWidth: 330,
    paddingTop: 20,
    paddingBottom: 20,
  },
  itemHeader: {
    maxWidth: 400,
    paddingTop: 44,
    paddingBottom: 24,
    [theme.breakpoints.down("sm")]: {
      paddingTop: 20,
      paddingBottom: 20,
    },
  },
  itemPassword: {
    paddingTop: 0,
    [theme.breakpoints.down("sm")]: {
      paddingBottom: 0,
    },
  },
  textField: {
    width: "100%",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  startButton: {
    width: "100%",
    maxWidth: 330,
    color: "#fff",
    fontWeight: "bold",
  },
}));

/**
 * queryMyself
 *
 * @type {string}
 */
const queryMyself = `
{
  readMyself {
    ID
    HashID
    Locale
    Email
    Firstname
    Surname
    Salutation
    StripePublicKey
    CreditCard {
      id
      name
      last4
      expired
    }
    Locations {
      edges {
        node {
          HashID
          Title
          Street
          Zip
          City
          Longitude
          Latitude
          Radius
          Version
          Sort
          Removed
        }
      }
    }
    Company {
      ID
      AdminUserID
      HashID
      FeatureSet
      ZipSearchAreas
      ZipSearchStatistic
      ApplicationFollowUpTimeInDays
      StripeIsTrialing
      StripeTrialingDays
      StripePlanID
      StripeValidThrough
      StripeProductID
      StripeCancelAt
      Name
      URLSegment
      ContactPerson
      Street
      Zip
      City
      Canton
      Distance
      Latitude
      Longitude
      Phone
      Mobile
      Fax
      Website
      Email
      NumberOfEmployees
      LegalForm
      FoundingYear
      MasterBusiness
      TrainingCompany
      BusinessActivityID
      BusinessSpecialities{
        edges {
          node {
            ID
            Title
            NogaCode
            SCCodeCompetence
            BoostScore
          }
        }
      }
      OtherBusinessActivities {
        edges {
          node {
            ID
          }
        }
      }
      OtherBusinessSpecialities {
        edges {
          node {
            ID
          }
        }
      }
      User{
        edges {
          node {
            HashID
            Email
            Firstname
            Surname
            Removed
            SearchAreas {
              HashID
              Title
              ZipList
            }
          }
        }
      }
      Memberships {
        edges {
          node {
            ID
            Title
            LogoUrl
          }
        }
      }
      AllMemberships {
        edges {
          node {
            ID
            Title
            LogoUrl
            IsBadge
          }
        }
      }
      MicrositeCertified
      MicrositeCertifiedUntil
      AdditionalMemberships
      MicrositeColorScheme
      MicrositeDescription
      MicrositeEmailApplicationText
      MicrositeImages {
        edges {
          node {
            Src
            Title
            Alt
          }
        }
      }
      MicrositeLogo {
        Src
        Title
        Alt
      }
      MicrositeReferences {
          edges {
            node {
              Sort
              Title
              Content
              Location
              Year
              Month
              ButtonLink
              ButtonTitle
              MicrositeReferencesImages {
              edges { 
                node {
                  Src
                  Title
                  Alt
                }
              }
            }
            }
          }
        }
        ApiAccess {
          edges {
            node {
              RequestUrl
            }
          } 
        }  
    }
    DossierTemplates {
      edges {
        node {
          HashID
          TemplateName
        }
      }
    }
    Permissions {
      Name
      RequireAdminStatus
    }
    FulltextSearchRequests {
      ID
      Title
    }
    SearchAreas {
      HashID
      Title
      ZipList
    }
  }
}
`;

/**
 * updateMyselfMutation
 *
 * @type {string}
 */
const updateMyselfMutation = `
  mutation UpdateMyself(
    $email: String!,
    $password: String
  ) {
    updateMyself(
      Email: $email,
      Password: $password
    ) {
      Email
    }
  }
`;

/**
 * OnboardingPasswordReset
 *
 * @param props
 * @returns {*}
 * @constructor
 */
const OnboardingPasswordReset = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    user,
    openWelcomeDialog,
    token,
    upgradeToPremiumClose,
    setMessage,
    unsetUser,
    unsetToken,
    setUser,
  } = useContext(GlobalContext);
  const [formValues, setFormValues] = useState({
    password: "",
    showPassword: false,
  });
  const [formValueErrors, setFormValueErrors] = useState({
    password: { hasError: false, message: "" },
  });
  const [{ fetching }, executeMutation] = useMutation(updateMyselfMutation);

  /**
   * Joi validation schema
   */
  const schema = Joi.object({
    password: Joi.string().required(),
    showPassword: Joi.boolean(),
  });

  /**
   * useEffect
   */
  useEffect(() => {
    if (props.id !== user.getData().hashID && user.isSet()) {
      unsetUser();
    }

    // After the upgrade-to-premium process redirect to start-page
    if (user.isSet() && upgradeToPremiumClose && !openWelcomeDialog) {
      navigate("/");
    }
  }, [props, user, upgradeToPremiumClose, openWelcomeDialog, unsetUser]);

  /**
   * Set the refresh-token to enable the ability
   * to fetch a token for the first time or on demand
   */
  if (!token.isSet()) {
    token.setRefreshToken(props.refreshToken);
  }

  /**
   * GraphQL query for user, gets triggered by calling "executeUsersQuery()"
   */
  const [result, executeMyselfQuery] = useQuery({
    query: queryMyself,
    variables: {},
    pause: true,
  });

  /**
   * Parse the permissions array from api to a single-dimensional array
   */
  const parsePermissions = (permissionsFromAPI) => {
    const permissionsArray = [];
    if (Array.isArray(permissionsFromAPI)) {
      for (let i = 0; i < permissionsFromAPI.length; i++) {
        permissionsArray.push(permissionsFromAPI[i].Name);
      }
    }
    return permissionsArray;
  };

  /**
   * GraphQL trigger and result handling
   */
  useEffect(() => {
    if (!result.fetching) {
      if (result.error) {
        // Check if the user need to be logged out
        if (result.error.message.indexOf("User forced logout") !== -1) {
          setMessage(MESSAGE_TYPES.ERROR, t("error.user.forced.logout"));
          unsetUser();
          unsetToken();
          navigate("/");
        } else {
          // Something went very wrong
          ExternalErrorLogger.log({
            text: "Error fetching user on OnboardingPasswordReset",
            data: {
              user: user.getData(),
              token: token.getData(),
              errorMessage: result.error.message,
            },
          });
          setMessage(MESSAGE_TYPES.ERROR, t("error.fetching.user"));
        }
      } else {
        // Query not fetching right now
        if (
          typeof result.data !== "undefined" &&
          typeof result.data.readMyself !== "undefined" &&
          !user.isSet()
        ) {
          const branchSubsData = [];
          result.data.readMyself[0].Company.BusinessSpecialities.edges.map(
            (item) => {
              branchSubsData.push(parseInt(item.node.ID));
              return branchSubsData;
            }
          );

          const memberships = [];
          result.data.readMyself[0].Company.Memberships.edges.map((item) => {
            memberships.push(item.node);
            return memberships;
          });

          const allMemberships = [];
          result.data.readMyself[0].Company.AllMemberships.edges.map((item) => {
            allMemberships.push(item.node);
            return allMemberships;
          });

          const locationData = [];
          result.data.readMyself[0].Locations.edges.map((item) => {
            const constructionSums = [];

            if (GetSafe(() => item.node.ConstructionSumList.edges)) {
              item.node.ConstructionSumList.edges.map((constructionSum) => {
                constructionSums.push(constructionSum.node.Code);
                return constructionSums;
              });
            }

            item.node.ConstructionSums = constructionSums;

            locationData.push(item.node);
            return locationData;
          });

          locationData.sort((a, b) => {
            return a.Sort - b.Sort;
          });

          const companyUsersData = [];
          result.data.readMyself[0].Company.User.edges.map((item) => {
            if (item.node.HashID !== result.data.readMyself[0].HashID) {
              companyUsersData.push(item.node);
            }
            return companyUsersData;
          });

          const dossierTemplatesData = [];
          result.data.readMyself[0].DossierTemplates.edges.map((item) => {
            dossierTemplatesData.push(item.node);
            return dossierTemplatesData;
          });

          companyUsersData.sort((a, b) => {
            if (a.Firstname.toLowerCase() < b.Firstname.toLowerCase()) {
              return -1;
            }
            if (a.Firstname.toLowerCase() > b.Firstname.toLowerCase()) {
              return 1;
            }
            return 0;
          });

          // Map the user-data
          user.setData({
            id: result.data.readMyself[0].ID,
            hashID: result.data.readMyself[0].HashID,
            locale: result.data.readMyself[0].Locale || "",
            email: result.data.readMyself[0].Email,
            firstName: result.data.readMyself[0].Firstname,
            lastName: result.data.readMyself[0].Surname,
            salutation: result.data.readMyself[0].Salutation || "",
            mode:
              result.data.readMyself[0].Company.FeatureSet === null
                ? null
                : result.data.readMyself[0].Company.FeatureSet.toLowerCase(),
            stripePublicKey: result.data.readMyself[0].StripePublicKey || "",
            creditCard: result.data.readMyself[0].CreditCard,
            locations: locationData || [],
            company: {
              id: result.data.readMyself[0].Company.ID,
              hashID: result.data.readMyself[0].Company.HashID,
              adminUserID: result.data.readMyself[0].Company.AdminUserID,
              name:
                result.data.readMyself[0].Company.Name || "TODO COMPANY-NAME", // TODO: Remove fallback company name for production (requires consistent data)
              street: result.data.readMyself[0].Company.Street || "",
              zip: result.data.readMyself[0].Company.Zip || "",
              city: result.data.readMyself[0].Company.City || "",
              canton: result.data.readMyself[0].Company.Canton || "",
              distance: result.data.readMyself[0].Company.Distance || 50,
              latitude: result.data.readMyself[0].Company.Latitude || null,
              longitude: result.data.readMyself[0].Company.Longitude || null,
              phone: result.data.readMyself[0].Company.Phone || "",
              mobile: result.data.readMyself[0].Company.Mobile || "",
              fax: result.data.readMyself[0].Company.Fax || "",
              email: result.data.readMyself[0].Company.Email || "",
              website: result.data.readMyself[0].Company.Website || "",
              employeeCount:
                result.data.readMyself[0].Company.NumberOfEmployees || "",
              legalForm: result.data.readMyself[0].Company.LegalForm || "",
              foundingYear:
                result.data.readMyself[0].Company.FoundingYear || "",
              masterOperation:
                FormatHelper.boolToString(
                  result.data.readMyself[0].Company.MasterBusiness
                ) || "",
              trainingCompany:
                FormatHelper.boolToString(
                  result.data.readMyself[0].Company.TrainingCompany
                ) || "",
              branchCategory:
                result.data.readMyself[0].Company.BusinessActivityID || "",
              branchSubs: branchSubsData || [],
              users: companyUsersData || [],
              stripeIsTrialing:
                result.data.readMyself[0].Company.StripeIsTrialing,
              stripeTrialingDays:
                result.data.readMyself[0].Company.StripeTrialingDays,
              stripePlanId: result.data.readMyself[0].Company.StripePlanID,
              stripeValidThrough:
                result.data.readMyself[0].Company.StripeValidThrough,
              stripeProductId:
                result.data.readMyself[0].Company.StripeProductID,
              stripeCancelAt: result.data.readMyself[0].Company.StripeCancelAt,
              stripeFuturePlans:
                result.data.readMyself[0].Company.StripeFuturePlans,
              memberships: memberships,
              allMemberships: allMemberships,
              zipSearchAreas: result.data.readMyself[0].Company.ZipSearchAreas,
              zipSearchStatistic: result.data.readMyself[0].Company.ZipSearchStatistic,
              applicationFollowUpTimeInDays: result.data.readMyself[0].Company.ApplicationFollowUpTimeInDays
            },
            dossierTemplates: dossierTemplatesData,
            permissions: parsePermissions(
              result.data.readMyself[0].Permissions
            ),
            fulltextSearchRequests:
              result.data.readMyself[0].FulltextSearchRequests,
            searchAreas: result.data.readMyself[0].SearchAreas,
          });

          setUser(user);
        } else {
          if (!user.isSet()) {
            executeMyselfQuery();
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  /**
   * handlePasswordChange
   *
   * @param event
   */
  const handlePasswordChange = (event) => {
    setFormValues({ ...formValues, password: event.target.value });
  };

  /**
   * handleClickShowPassword
   */
  const handleClickShowPassword = () => {
    setFormValues({ ...formValues, showPassword: !formValues.showPassword });
  };

  /**
   * handleFormSubmit
   */
  const handleFormSubmit = () => {
    const { error } = schema.validate(formValues, { abortEarly: false });
    if (error) {
      const formErrors = FormValueErrorsBuilder(error, t);
      setFormValueErrors({ ...formErrors });
    } else {
      executeMutation({
        email: user.getData().email,
        password: formValues.password,
      }).then((result) => {
        if (result.error) {
          // Check if the user need to be logged out
          if (result.error.message.indexOf("User forced logout") !== -1) {
            setMessage(MESSAGE_TYPES.ERROR, t("error.user.forced.logout"));
            unsetUser();
            unsetToken();
            navigate("/");
          } else {
            setMessage(MESSAGE_TYPES.ERROR, t("error.save"));
            ExternalErrorLogger.log({
              text: "Error save new password on OnboardingPasswordReset",
              data: {
                user: user.getData(),
                token: token.getData(),
                errorMessage: result.error.message,
              },
            });
          }
        } else {
          executeMyselfQuery();
          navigate("/");
        }
      });
    }
  };

  /**
   * LoadingIndicator
   */
  const LoadingIndicator = (
    <Grid item>
      <CircularProgress />
    </Grid>
  );

  /**
   * UserNotFoundDialog
   */
  const UserNotFoundDialog = (
    <Grid item>
      <div>{t("error.fetching.user")}</div>
    </Grid>
  );

  /**
   * PasswordResetDialog
   */
  const PasswordResetDialog = (
    <>
      <Grid item className={clsx(classes.item, classes.itemHeader)}>
        <h1 className={classes.title}>{t("general.label.set.new.password")}</h1>
      </Grid>
      <Grid item className={clsx(classes.item, classes.itemPassword)}>
        <TextField
          id="outlined-adornment-password"
          className={classes.textField}
          variant="outlined"
          align="left"
          error={GetSafe(() => formValueErrors.password.hasError, false)}
          type={formValues.showPassword ? "text" : "password"}
          label={t("form.label.password")}
          value={formValues.password}
          helperText={GetSafe(() => formValueErrors.password.message, "")}
          onChange={handlePasswordChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  color="primary"
                  aria-label="Toggle password visibility"
                  onClick={handleClickShowPassword}
                >
                  {formValues.showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item className={classes.item}>
        <Button
          id="buttonFormSubmit"
          variant="contained"
          color="primary"
          className={classes.startButton}
          onClick={handleFormSubmit}
          disabled={fetching}
        >
          {t("general.label.save")}
          {fetching && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Button>
      </Grid>
    </>
  );

  /**
   * return
   */
  return (
    <>
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justify="center"
      >
        {result.fetching && LoadingIndicator}
      </Grid>
      {!result.fetching && (
        <Dialog
          fullWidth
          maxWidth="md"
          className={classes.root}
          open={openWelcomeDialog}
        >
          <DialogContent className={classes.dialog}>
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justify="center"
            >
              {user.isSet() && PasswordResetDialog}
              {!user.isSet() && UserNotFoundDialog}
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default withAccessControl(
  OnboardingPasswordReset,
  ACL_COMPONENTS.ONBOARDING
);
